import Vue from 'vue'
import {ActionTree, MutationTree} from 'vuex'

import sdk from '@/lib/kepler/sdk'
import {
  AvailabilityRequest,
  BookRequest, EstimateRequest,
  KeepRequest,
  OpenVehicleRequest,
  PaginatedRequest,
  ReservationEditRequest,
  ReservationExtendRequest,
  ReservationFlag,
  ReservationPaginatedResponse,
  ReservationResponse,
  SearchRequest,
  TerminateRequest,
  TrackRequest,
  VehicleCategory,
  VehicleSlot,
  RatingRequest,
  BookingMesh,
} from '@/lib/kepler/interfaces'
import ServiceMesh from '@/lib/serviceMesh'
import {EventBus} from '@/main'
import {RootState} from '@/store'
import {AxiosError} from 'axios'

function sameReservationsArrays(res1: ReservationResponse[] = [], res2: ReservationResponse[] = []) {
  if (res1.length !== res2.length) {
    return false
  }
  const createMap = (res: ReservationResponse[]) => new Map(res.map((r) => [r.id, r]))
  const res1Map = createMap(res1)
  const res2Map = createMap(res2)

  const comparePos = (r1: ReservationResponse, r2?: ReservationResponse) => {
    const sameLat = r1.current_vehicle_position?.latitude === r2?.current_vehicle_position?.latitude
    const sameLng = r1.current_vehicle_position?.longitude === r2?.current_vehicle_position?.longitude
    return sameLat && sameLng
  }

  const sameRes = (r1: ReservationResponse, r2?: ReservationResponse) => {
    return r2 !== undefined &&
    (r1.updated_timestamp === r2.updated_timestamp) && // sameUpdated
    (r1.start_timestamp === r2.start_timestamp) && // sameStart
    (r1.end_timestamp === r2.end_timestamp) && // sameEnd
    (r1.status === r2.status) && // sameStatus
    comparePos(r1, r2) && // samePosition
    (JSON.stringify(r1.extra) === JSON.stringify(r2.extra))// sameExtra
  }

  for (const [key, val] of res1Map) {
    if (res2Map.get(key) === undefined || !sameRes(val, res2Map.get(key))) {
      return false
    }
  }
  return true
}

export class BookingSearchRequest {
  public searchRequest: SearchRequest
  public layerKey: string

  constructor(searchRequest: SearchRequest, layerKey: string) {
    this.searchRequest = searchRequest
    this.layerKey = layerKey
  }
}

export class Layer {
  public bookingMode: string
  public key: string
  public vehicles: VehicleSlot[]

  constructor(bookingMode: string, vehicles: VehicleSlot[], key: string) {
    this.bookingMode = bookingMode
    this.vehicles = vehicles
    this.key = key
  }
}

export class BookingState {
  public layers: { [key: string]: Layer } = {}
  public activeReservations: ReservationResponse[] = []
  public selectedReservation!: ReservationResponse | null
  public terminateMode: boolean = false
  public terminateModeMessages: string[] = []
  public meshes!: BookingMesh[]
  public bookingFlags: ReservationFlag[] = []
  public categories!: VehicleCategory[]

  public reservations: ReservationPaginatedResponse = {current_page: 0, items: []}
  public driversReservations: ReservationPaginatedResponse = {current_page: 0, items: []}
  public reservationPerPage: number = 10
  public futureReservations: ReservationResponse[] = []
  public refreshRunning: boolean = false
  public unlockedReservations: Array<string | number> = []
}

let pool: NodeJS.Timeout

function cronCurrent(fun: () => void): void {
  fun()
  pool = setTimeout(() => {
    cronCurrent(fun)
  }, 1000 * 10)
}

const mutations: MutationTree<BookingState> = {
  BOOKING_ADD_LAYER(thisState, payload: Layer) {
    // https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
    const tempLayers: { [key: string]: Layer } = {}
    tempLayers[payload.key] = payload
    thisState.layers = Object.assign({}, thisState.layers, tempLayers)
  },
  FLUSH(flush) {
    flush.layers = {}
    flush.activeReservations = []
    // flush.meshes = []
    flush.bookingFlags = []
    flush.reservations = {current_page: 0, items: []}
    flush.futureReservations = []
    flush.driversReservations = {current_page: 0, items: []}
  },
  BOOKING_LAYERS_FLUSH(layersFlush) {
    Vue.delete(layersFlush, 'layers')
    Vue.set(layersFlush, 'layers', {})
  },
  BOOKING_SELECT_RESERVATION(reservation, payload: ReservationResponse) {
    reservation.selectedReservation = null
    reservation.selectedReservation = payload
  },
  BOOKING_UNSET_SELECTED_RESERVATION(reservation) {
    reservation.selectedReservation = null
  },
  BOOKING_SET_ACTIVE_RESERVATIONS(activeState, payload: ReservationResponse[]) {
    if (!sameReservationsArrays(activeState.activeReservations, payload)) {
      activeState.activeReservations = payload
    }
  },
  BOOKING_SET_MESHES(meshes, payload: ServiceMesh[]) {
    meshes.meshes = payload
  },
  BOOKING_SET_CATEGORIES(stateB, payload: VehicleCategory[]) {
    stateB.categories = payload
  },
  BOOKING_TERMINATE_MODE_ON(reservation, payload: string[]) {
    reservation.terminateModeMessages = payload
  },
  BOOKING_TERMINATE_MODE_OFF(reservation) {
    reservation.terminateMode = false
  },
  BOOKING_SET_FLAG(reservation, payload: ReservationFlag) {
    const flag: ReservationFlag = {
      reservation_number: payload.reservation_number,
      value: payload.value,
      flag: 'DONT_CLOSE_BEFORE',
    }
    reservation.bookingFlags.push(flag)
  },
  BOOKING_FLUSH_FLAGS(reservation) {
    reservation.bookingFlags = []
  },
  BOOKING_RESERVATIONS(reservation, payload: ReservationPaginatedResponse) {
    reservation.reservations.current_page = payload.current_page
    reservation.reservations.current_page_count = payload.current_page_count
    payload.items.forEach((item) => {
      reservation.reservations.items.push(item)
    })

    reservation.reservations.items_count = payload.items_count
    reservation.reservations.page_count = payload.page_count
    reservation.reservations.per_page_count = payload.per_page_count
  },
  BOOKING_RESERVATIONS_FLUSH(reservation) {
    reservation.reservations = {current_page: 0, items: []}
  },
  BOOKING_FUTURE_RESERVATIONS(reservation, payload: ReservationResponse[]) {
    reservation.futureReservations = payload
  },
  BOOKING_DRIVERS_RESERVATIONS(reservation, payload: ReservationPaginatedResponse) {
    reservation.driversReservations.current_page = payload.current_page
    reservation.driversReservations.current_page_count = payload.current_page_count
    payload.items.forEach((item) => {
      reservation.driversReservations.items.push(item)
    })

    reservation.driversReservations.items_count = payload.items_count
    reservation.driversReservations.page_count = payload.page_count
    reservation.driversReservations.per_page_count = payload.per_page_count
  },
  BOOKING_DRIVERS_RESERVATIONS_FLUSH(reservation) {
    reservation.driversReservations = {current_page: 0, items: []}
  },
  BOOKING_START_CRON(reservation, func) {
    // console.log('cron 10s called, status', !reservation.refreshRunning && !!reservation.activeReservations.length)
    if (!reservation.refreshRunning && reservation.activeReservations.length) {
      reservation.refreshRunning = true
      cronCurrent(func)
      // console.log('cron 10s launched')
    } else if (!reservation.activeReservations.length) {
      clearTimeout(pool)
      reservation.refreshRunning = false
      // console.log('cron 10s stopped')
    }
  },
  BOOKING_STOP_CRON(reservation) {
    reservation.refreshRunning = false
  },
  OPEN_VEHICLE_IN_RESERVATION_ADD(reservation, resnum: number) {
    if (typeof reservation.unlockedReservations !== 'object') {
      Vue.set(reservation, 'unlockedReservations', [])
    }
    reservation.unlockedReservations.push(resnum)
    if (reservation.unlockedReservations.length > 10) {
      reservation.unlockedReservations.shift()
    }
  },
}

const actions: ActionTree<BookingState, RootState> = {
  init({dispatch, commit}) {
    dispatch('serviceMeshes')
    dispatch('categories')
    dispatch('current')
    dispatch('unsetSelectReservation')
    dispatch('terminateModeOff')
    commit('BOOKING_STOP_CRON')
  },
  bookingSearch({commit}, payload: BookingSearchRequest) {
    return sdk.booking.search(payload.searchRequest).then((r) => {
      return commit('BOOKING_ADD_LAYER', new Layer(payload.searchRequest.booking_mode!, r.data, payload.layerKey))
    })
  },
  bookingPublicSearch({commit}, payload: BookingSearchRequest) {
    return sdk.booking.publicSearch(payload.searchRequest).then((r) => {
      return commit('BOOKING_ADD_LAYER', new Layer(payload.searchRequest.booking_mode!, r.data, payload.layerKey))
    })
  },
  layersFLush({commit}) {
    return commit('BOOKING_LAYERS_FLUSH')
  },
  bookingFlush({commit}) {
    return commit('FLUSH')
  },
  book({dispatch}, payload: BookRequest) {
    return sdk.booking.book(payload).then((r) => {
      dispatch('setZone', r.data.vehicle_slot.id)
      dispatch('refreshHistory')
      dispatch('sleep')
      return r.data
    })
  },
  // status.booking.sent
  purge({commit, dispatch}) {
    dispatch('layersFLush')
    commit('BOOKING_DRIVERS_RESERVATIONS_FLUSH')
    return commit('FLUSH')
  },
  current({rootState, dispatch, commit}): Promise<ReservationResponse[]> | undefined {
    if (rootState.userToken) {
      return sdk.booking.current().then((r) => {
        const reservations = r.data
        commit('BOOKING_SET_ACTIVE_RESERVATIONS', reservations)
        reservations.forEach((reservation) => {
          dispatch('setZone', reservation.vehicle_slot.id)
        })
        dispatch('sleep')
        commit('BOOKING_START_CRON', () => {
          dispatch('current')
        })
        return r.data
      })
    }
  },
  endReservation({dispatch}, payload: ReservationResponse) {
    return sdk.booking.end(payload).then(() => {
      dispatch('refreshHistory')
    })
  },
  openVehicle({dispatch}, payload: OpenVehicleRequest) {
    dispatch('refreshHistory')
    return sdk.booking.open(payload).catch(() => {
      EventBus.$emit('tryOpenIfInvers')
    })
  },
  closeVehicle({}, payload: ReservationResponse) {
    return sdk.booking.close(payload.number).catch(() => {
      EventBus.$emit('tryCloseIfInvers')
    })
  },
  getReservation({}, payload: ReservationResponse) {
    return sdk.booking.detail(payload).then((r) => r.data)
  },
  getReservationByNumber({}, reservationNumber: number) {
    return sdk.booking.getByNumber(reservationNumber).then((r) => r.data)
  },
  serviceMeshes({dispatch, commit}) {
    return sdk.booking.serviceMesh().then((r) => {
      commit('BOOKING_SET_MESHES', r.data)
      dispatch('sleep')
    })
  },
  selectReservation({commit}, payload: ReservationResponse): Promise<ReservationResponse> {
    return new Promise((resolve) => {
      commit('BOOKING_SELECT_RESERVATION', payload)
      resolve(payload)
    })
  },
  unsetSelectReservation({commit}) {
    commit('BOOKING_UNSET_SELECTED_RESERVATION')
  },
  zones({}, id: string) {
    return sdk.booking.zones(id).then((r) => r.data)
  },
  terminateModeOn({commit}, payload: string[]) {
    commit('BOOKING_TERMINATE_MODE_ON', payload)
  },
  terminateModeOff({commit}) {
    commit('BOOKING_TERMINATE_MODE_OFF')
  },
  terminateReservation({dispatch}, payload: TerminateRequest) {
    return new Promise((resolve, reject) => {
      sdk.booking.terminate(payload)
        .then((response) => {
          dispatch('refreshHistory')
          resolve(response)
        }, (error: AxiosError) => {
          // here could be dragons
          if (error.response !== undefined) {
            if (error.response.status === 409) {
              const message: [string] = error.response.data.messages
              dispatch('terminateModeOn', message)
            }
          }
          reject(error)
        })
    })
  },
  track({}, payload: TrackRequest) {
    return sdk.booking.track(payload)
  },
  keep({dispatch}, payload: KeepRequest) {
    return sdk.booking.keep(payload).then(() => {
      dispatch('bookingFlags', payload)
      dispatch('sleep')
    })
  },
  bookingFlags({commit}, payload: ReservationFlag) {
    commit('BOOKING_SET_FLAG', payload)
  },
  bookingFlushFlags({dispatch, commit}) {
    commit('BOOKING_FLUSH_FLAGS')
    dispatch('sleep')
  },
  categories({dispatch, commit}) {
    return sdk.booking.publicCategories().then((r) => {
      commit('BOOKING_SET_CATEGORIES', r.data)
      dispatch('sleep')
    })
  },
  reservationHistoryDefault({commit}) {
    commit('BOOKING_RESERVATIONS_FLUSH')
    return new Promise((resolve, reject) => {
      sdk.people.reservations({
        per_page: 10,
        page_number: 1,
      }).then((response) => {
        commit('BOOKING_RESERVATIONS', response.data)
        resolve(response)
      }).catch(reject)
    })
  },
  flushAndReloadHistory({dispatch, commit}) {
    commit('BOOKING_RESERVATIONS_FLUSH')
    commit('BOOKING_FUTURE_RESERVATIONS', [])
    dispatch('futureReservations')
    return dispatch('reservationHistory', {per_page: 10, page_number: 1})
  },
  futureReservations({commit}) {
    return sdk.booking.futureReservations().then((response) => {
      commit('BOOKING_FUTURE_RESERVATIONS', response.data)
    })
  },
  deleteReservation({dispatch}, payload: ReservationResponse) {
    return sdk.booking.deleteReservation(payload).then(() => {
      return dispatch('refreshHistory')
    })
  },
  editReservation({dispatch}, payload: ReservationEditRequest) {
    return sdk.booking.editReservation(payload).then((r) => {
      dispatch('refreshHistory')
      return r.data
    })
  },
  extendReservation({dispatch}, payload: ReservationExtendRequest) {
    return sdk.booking.extendReservation(payload).then((data) => {
      dispatch('refreshHistory')
      return data.data
    })
  },
  vehicleAvailability({}, payload: AvailabilityRequest) {
    return sdk.booking.availabilty(payload).then((r) => {
      r.data.forEach((a) => {
        Object.assign(a, {selected: false})
      })
      return r.data
    })
  },
  bookEstimate({}, payload: EstimateRequest) {
    return sdk.booking.estimate(payload).then((r) => {
      return r.data
    })
  },
  refreshHistory({dispatch, commit}) {
    commit('BOOKING_RESERVATIONS_FLUSH')
    dispatch('current')
    dispatch('reservationHistory', {per_page: 10, page_number: 1})
    dispatch('futureReservations')
  },
  getFuelPin({}, reservationNumber: number) {
    return sdk.booking.getFuelPin(reservationNumber).then((r) => r.data)
  },
  reservationHistory({commit}, payload: PaginatedRequest) {
    return new Promise((resolve) => {
      sdk.people.reservations(payload).then((response) => {
        commit('BOOKING_RESERVATIONS', response.data)
        resolve(response)
      })
    })
  },
  myDriversReservations({commit}, payload: PaginatedRequest) {
    return new Promise((resolve) => {
      sdk.people.myDriversReservations(payload).then((response) => {
        commit('BOOKING_DRIVERS_RESERVATIONS', response.data)
        resolve(response)
      })
    })
  },
  flushAndReloadDriversReservationsHistory({dispatch, commit}) {
    commit('BOOKING_DRIVERS_RESERVATIONS_FLUSH')
    return dispatch('myDriversReservations', {per_page: 10, page_number: 1})
  },
  bookingRating({}, payload: RatingRequest) {
    const {ratings, reservation} = payload
    return sdk.booking.rating(reservation, ratings)
  },
  hasOpenedVehicleInReservation({state, commit}, payload: { resnum: number, add?: boolean }) {
    if (payload.add) {
      commit('OPEN_VEHICLE_IN_RESERVATION_ADD', payload.resnum)
    }
    return Promise.resolve(state.unlockedReservations?.includes(payload.resnum))
  },
  sendExtraAction({}, payload: { reservationNumber: number, operation: string }) {
    return sdk.reservation.sendCommand(payload.reservationNumber, payload.operation).then((r) => r.data)
  },
  checkupReservation({}, resNum: number) {
    return sdk.reservation.checkup(resNum).then((r) => r.data)
  },
}

export default {
  state: new BookingState(),
  mutations,
  actions,
}
